/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  CallingIdentity,
  Direction,
  LookupMappings,
  useCreatePromptRevision,
  useGetPromptRevision,
  useGetPublishedPromptRevision,
  useListLookupMappings,
  useUpdatePromptRevision,
} from '@agent-assist/api-typescript-react-query-hooks';
import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {CriteriaDurationPicker} from '../../../components/CriteriaDurationPicker';
import {LoadingSpinner} from '../../../components/LoadingSpinner';
import {PageContainer} from '../../../components/PageContainer';
import {PromptForm} from '../../../components/QualityAssurance/PromptForm';
import {LegacyButton} from '../../../components/ui/legacy-button';
import {usePromptCreateModal} from '../../../hooks/modal/prompt-create-modal';
import {isApiError} from '../../../hooks/useDefaultApiClient';
import {useToast} from '../../../hooks/useToast';
import {
  setPromptEditMode,
  setPromptRevision,
  setUpdatedPromptRevision,
  usePromptDispatch,
  usePromptState,
} from '../../../providers/PromptProvider';
import {shortenUuid} from '../../../services/formatString';
import {useUserStore} from '../../../stores/userStore';

const directions = [
  {value: 'inbound', label: 'Inbound'},
  {value: 'outbound', label: 'Outbound'},
];

export const MetadataFilters = ({
  selectedQueues,
  selectedDirection,
  onQueuesChange,
  onDirectionChange,
  callDuration,
  onDurationChange,
}: {
  selectedQueues: LookupMappings;
  selectedDirection: string;
  onQueuesChange: (queues: LookupMappings) => void;
  onDirectionChange: (direction: string) => void;
  callDuration: number;
  onDurationChange: (duration: number) => void;
}) => {
  const {data: queueMappings, isLoading: isQueueMappingsLoading} = useListLookupMappings(
    {
      lookupType: 'QUEUE',
    },
    {enabled: true},
  );
  const [deselectedQueues, setDeselectedQueues] = useState<LookupMappings>({});
  const availableQueues = queueMappings?.mappings || {};
  const allQueues = {...selectedQueues, ...availableQueues, ...deselectedQueues};
  const getQueueNames = useCallback(
    (mappings: LookupMappings) => {
      if (!mappings) return '';
      if (Object.values(mappings).length > 0) {
        const queueNames = Object.values(mappings).join(', ');
        return queueNames;
      }

      // look them up in queueMappings (Create mode)
      if (queueMappings?.mappings) {
        const queueNames = Object.keys(mappings)
          .map((id) => queueMappings.mappings[id])
          .filter(Boolean)
          .join(', ');
        return queueNames;
      }

      return '';
    },
    [queueMappings],
  );
  return (
    <Card sx={{flex: 1}}>
      <CardHeader title={'Metadata Filters'} />
      <CardContent className={'flex gap-4 mb-4 align-items-flex-end'}>
        <FormControl fullWidth>
          {isQueueMappingsLoading ? (
            <Select label={'QueueIds'}>
              <MenuItem>
                {'Loading... '}
                <LoadingSpinner size={'sm'} />
              </MenuItem>
            </Select>
          ) : Object.keys(queueMappings?.mappings || {}).length === 0 ? (
            <TextField
              fullWidth
              disabled
              value={'All Queue IDs are in use by other prompts'}
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'gray',
                },
              }}
            />
          ) : (
            <>
              <InputLabel>QueueIds</InputLabel>
              <Select
                sx={{height: 56}}
                multiple
                value={Object.keys(selectedQueues)}
                label={'QueueIds'}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                onChange={(e) => {
                  const selectedIds = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  if (queueMappings?.mappings) {
                    const newMappings: LookupMappings = {};
                    const newDeselected: LookupMappings = {};

                    Object.entries(allQueues).forEach(([id, name]) => {
                      if (selectedIds.includes(id)) {
                        newMappings[id] = name;
                      } else if (selectedQueues[id]) {
                        newDeselected[id] = name;
                      }
                    });
                    setDeselectedQueues(newDeselected);
                    onQueuesChange(newMappings);
                  }
                }}
                renderValue={() => <div>{getQueueNames(selectedQueues)}</div>}
              >
                {isQueueMappingsLoading && <LoadingSpinner size={'sm'} />}
                {Object.entries(allQueues).map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    <Checkbox checked={Boolean(selectedQueues?.[id])} />
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </FormControl>

        <FormControl fullWidth sx={{maxHeight: 56}}>
          <InputLabel>Direction</InputLabel>
          <Select
            sx={{height: 56}}
            value={selectedDirection}
            label={'Direction'}
            onChange={(e) => onDirectionChange(e.target.value)}
          >
            {directions.map(({value, label}) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CriteriaDurationPicker duration={callDuration} onChange={onDurationChange} />
      </CardContent>
    </Card>
  );
};

export const CreateQAPrompts = () => {
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const username = useUserStore((state) => state.username);
  const identityId = useUserStore((state) => state.userId);
  const callingIdentity = {identityId, username};

  const {updatedPromptRevision, isUpdatedPromptRevisionGroupsValid} = usePromptState();
  const dispatch = usePromptDispatch();
  const [totalQuestions, setTotalQuestions] = useState<number>();
  const navigate = useNavigate();
  const [promptName, setPromptName] = useState<string>(updatedPromptRevision.name);
  const sections = Object.keys(updatedPromptRevision.promptGroups);
  const createQAPrompt = useCreatePromptRevision();
  const updatePrompt = useUpdatePromptRevision();
  const {promptRevisionId} = useParams<{promptRevisionId: string}>();
  const publishPrompt = useGetPublishedPromptRevision();
  const {toastError} = useToast();
  const promptRevision = useGetPromptRevision({criteriaId: promptRevisionId!}, {enabled: Boolean(promptRevisionId)});

  const [isReady, setIsReady] = useState(promptRevisionId ? false : true);

  useEffect(() => {
    const totalCount: number = Object.values(updatedPromptRevision.promptGroups).reduce(
      (previousValue, currentValue) => {
        return previousValue + currentValue.questions.length;
      },
      0,
    );
    setTotalQuestions(totalCount);
  }, [updatedPromptRevision]);

  useEffect(() => {
    if (
      promptRevision &&
      promptRevision.data &&
      promptRevision?.data &&
      promptRevisionId === promptRevision.data?.promptRevisionId
    ) {
      setPromptEditMode(dispatch, true);
      setPromptRevision(dispatch, promptRevision.data);
      setUpdatedPromptRevision(dispatch, promptRevision.data);
      setIsReady(true);
      // without this if condition, the create prompt test fails
      if (promptRevision.data.name) setPromptName(promptRevision.data.name);
    }
  }, [promptRevisionId, promptRevision?.data?.promptRevisionId]);

  const onSave = useCallback(async () => {
    const payload = {
      ...updatedPromptRevision,
      name: promptName,
    };
    try {
      if (promptRevisionId) {
        let updatedPromptDetails;
        updatedPromptDetails = await updatePrompt.mutateAsync({
          promptRevisionId,
          updatePromptRevisionRequestContent: {
            ...payload,
            callingIdentity: callingIdentity as CallingIdentity,
          },
        });
        if (updatedPromptDetails) navigate(`/prompts/${updatedPromptDetails.promptRevisionId}`);
      } else {
        const newPrompt = await createQAPrompt.mutateAsync({
          createPromptRevisionRequestContent: {
            ...payload,
            callingIdentity: callingIdentity as CallingIdentity,
          },
        });
        navigate(`/prompts/${newPrompt.promptRevisionId}`);
      }
    } catch (error) {
      if (isApiError(error)) {
        if (error.details.message === 'A prompt revision with duration filter already exists.') {
          toastError('A prompt revision with duration filter already exists.');
        } else {
          toastError(error.details.message || 'An error occurred while saving the prompt');
        }
      } else {
        toastError('An unexpected error occurred');
      }
    }
  }, [
    updatedPromptRevision,
    navigate,
    promptName,
    toastError,
    promptRevisionId,
    updatePrompt,
    createQAPrompt,
    callingIdentity,
  ]);
  const publishConfirmation = usePromptCreateModal({
    onConfirm: onSave,
    onChangeName: setPromptName,
    name: promptName,
  });

  const isEditPage = !!promptRevisionId;

  if (!isReady) return null;

  const allItemsValid = Object.values(isUpdatedPromptRevisionGroupsValid).every(Boolean) && totalQuestions !== 0;
  const disableButtons = !allItemsValid || createQAPrompt.isLoading || publishPrompt.isLoading;

  const action = (
    <>
      <LegacyButton
        onClick={() => {
          promptRevisionId ? navigate(`/prompts/${promptRevisionId}`) : navigate(`/prompts`);
        }}
        variant={'secondary'}
      >
        Cancel
      </LegacyButton>
      <LegacyButton
        disabled={disableButtons || !permissionsMap[isEditPage ? PERMISSIONS.PROMPT_UPDATE : PERMISSIONS.PROMPT_CREATE]}
        onClick={() => {
          publishConfirmation.show();
        }}
        variant={'default'}
      >
        Save
      </LegacyButton>
    </>
  );

  return (
    <PageContainer
      title={
        promptRevisionId
          ? `Editing ${shortenUuid(promptRevisionId).replace('prompt-', 'criteria-')}`
          : 'Create Criteria'
      }
      actions={action}
    >
      {publishConfirmation.modal}
      <MetadataFilters
        onQueuesChange={(queueIds: LookupMappings) => {
          setUpdatedPromptRevision(dispatch, {...updatedPromptRevision, queueIds});
        }}
        onDirectionChange={(direction: string) => {
          setUpdatedPromptRevision(dispatch, {...updatedPromptRevision, initialDirection: direction as Direction});
        }}
        selectedQueues={updatedPromptRevision.queueIds || {}}
        selectedDirection={updatedPromptRevision.initialDirection || ''}
        callDuration={updatedPromptRevision.duration || 0}
        onDurationChange={(duration: number) => {
          setUpdatedPromptRevision(dispatch, {...updatedPromptRevision, duration});
        }}
      />
      <PromptForm sections={sections} />
      {/* // POC to add a new group:
        <Button
          onClick={() => {
            const data = { ...updatedPromptRevision };
            const newGroup = { promptGroupType: "qa", questions: [], totalWeight: 0 };
            data.promptGroups = {
              ...data.promptGroups,
              NEWGROUPNAME: newGroup,
            };
            setUpdatedPromptRevision(dispatch, data);
          }}
          variant={"default"}
        >
          Add new Section
        </Button>
      */}
    </PageContainer>
  );
};

export default CreateQAPrompts;
